<script lang="ts" setup>
import { computed, ref } from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { MassRetagFormData } from '@modules/mass-retag/types';
import type { MirrorAddFileFormData } from '@modules/mirrors-list/submodules/create/submodules/file/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useMirrorsListNamespace } from '@modules/mirrors-list/composables';
import { MIRRORS_LIST_JS_MAIN_ROUTE_NAME, MIRRORS_LIST_NO_JS_MAIN_ROUTE_NAME } from '@modules/mirrors-list/constants';
import {
  TRANSLATION_GROUP_MIRRORS_CREATE_FILE,
} from '@modules/mirrors-list/submodules/create/submodules/file/constants';
import { useMirrorAddFileMutation } from '@modules/mirrors-list/submodules/create/submodules/file/mutations';

const { getTranslation } = useI18n();
const { isMirrorJsNamespace, mirrorNamespaceType } = useMirrorsListNamespace();

const formData = ref<MassRetagFormData>();

const {
  mutateAsync: sendFile,
  errors,
  clearError,
  isLoading,
} = useMirrorAddFileMutation();

const backRouteName = computed(() => (
  isMirrorJsNamespace.value ? MIRRORS_LIST_JS_MAIN_ROUTE_NAME : MIRRORS_LIST_NO_JS_MAIN_ROUTE_NAME
));

const submitBtnLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_FILE, 'JS_SAVE_BTN').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_FILE, 'NO_JS_SAVE_BTN').value
));

const backBtnLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_FILE, 'JS_BACK_BTN').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_FILE, 'NO_JS_BACK_BTN').value
));

const inputLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_FILE, 'JS_FROM_FILE_HEADER').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_FILE, 'NO_JS_FROM_FILE_HEADER').value
));

const dragAndDropLabel = computed(() => (
  isMirrorJsNamespace.value
    ? getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_FILE, 'JS_FROM_FILE_DESCRIPTION').value
    : getTranslation(TRANSLATION_GROUP_MIRRORS_CREATE_FILE, 'NO_JS_FROM_FILE_DESCRIPTION').value
));

const formSchema = computed<FormSchema<MirrorAddFileFormData>>(() => ({
  file: {
    type: EFormInputTypes.SINGLE_FILE,
    label: inputLabel.value,
    required: true,
    value: formData.value?.file,
    dragAndDropLabel: dragAndDropLabel.value,
  },
}));

async function submitHandler(data: MirrorAddFileFormData): Promise<void> {
  formData.value = data;
  const form = new FormData();

  for (const [name, value] of Object.entries(data)) {
    if (value) {
      form.append(name, value);
    }
  }

  await sendFile({ file: form, mirror_type: mirrorNamespaceType.value });
  formData.value = undefined;
}
</script>

<template>
  <VForm v-data-qa-auto-id="'MirrorAddFilePage'"
    :schema="formSchema"
    :submit-btn-label="submitBtnLabel"
    :custom-errors="errors"
    :is-loading="isLoading"
    @submit="submitHandler($event)"
    @interacted="clearError"
  >
    <template #secondaryBtn>
      <VBackButton
        :label="backBtnLabel"
        :to="{ name: backRouteName }"
      />
    </template>
  </VForm>
</template>
